/*
 * Single data seam for every screen. There is NO mock/demo source anywhere
 * (Duncan directive BOARDROOM-2026-08-04-084237): a signed-in user sees REAL
 * stage data or an honest loading/empty/unavailable state, full stop.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { loadSession, clearSession, storeSession, type Session } from '../auth/session'
import { AuthFlowError, refreshSession } from '../auth/cognitoAuth'
import { redirectToSignIn } from '../auth/sessionExpiry'
import { ApiAuthError, fetchConsumerOrders, ticketsApiBase, type LiveData, type LiveDataState } from './api'
import { fetchOrgBranding, isOrgUnitUuid, orgBrandingApiBase, type OrgBrandingResolved } from './orgBranding'
import type { Order, Ticket, TrEvent } from './types'

interface DataContextValue {
  liveState: LiveDataState
  orders: Order[]
  tickets: Ticket[]
  events: TrEvent[]
  eventById: (id: string) => TrEvent | undefined
  ticketsForOrder: (orderId: string) => Ticket[]
  /** Resolved org-unit branding (settings' central-asset-store read,
   *  BOARDROOM-2026-08-28-110128). undefined = not (yet) fetched;
   *  null = org unit unknown (404). Best-effort — consumers keep the
   *  neutral fallback on undefined/null and on any null field. */
  orgBranding: (orgUnitId: string) => OrgBrandingResolved | null | undefined
  sessionEmail?: string
  refresh: () => void
}

const DataContext = createContext<DataContextValue | null>(null)

/**
 * BOARDROOM-105741 — self-healing reauth. On a 401 the OLD code reloaded the
 * page on the SAME stale id token (gated by a 15s throttle), which left a user
 * with an expired token — or a token minted before a server-side authorizer fix
 * — PERMANENTLY on the "unavailable" holding card with no clean recovery.
 *
 * Instead: exchange the stored refresh token for a FRESH session (Cognito
 * REFRESH_TOKEN_AUTH — re-mints the id token from the account's current state),
 * persist it, and RE-FETCH orders with the new id token. A normal token expiry
 * now self-heals with no page reload.
 *
 * FAILURES ARE DISCRIMINATED (CMT-2026-09-03-026 — redirect only AFTER refresh
 * fails, and only when the session is GENUINELY dead):
 *  - 'no-refresh-token' — the API rejected the id token and nothing can heal
 *    it: session end.
 *  - 'refresh-rejected' — Cognito refused the refresh token (revoked/expired):
 *    session end.
 *  - 'refresh-transient'  — the refresh call failed on NETWORK: evidence about
 *    that call, not the session; never a sign-out.
 *  - 'refetch-rejected' — the FRESH token was ALSO rejected (a genuine
 *    authorizer reject, not a stale token).
 *  - 'refetch-failed' — refresh SUCCEEDED (session is healed + stored) but the
 *    re-fetch itself failed non-auth; an ordinary data failure, never sign-out.
 */
type RefreshOutcome =
  | { ok: true; data: LiveData }
  | {
      ok: false
      reason: 'no-refresh-token' | 'refresh-rejected' | 'refresh-transient' | 'refetch-rejected' | 'refetch-failed'
      /** The id token OUR refresh minted (refetch-* reasons) — lets the
       *  healed-elsewhere guard below tell a watcher heal from our own. */
      mintedIdToken?: string
    }

/** Session end (dead/absent/unrefreshable) vs every recoverable/ambiguous case. */
function isSessionEnd(reason: Exclude<RefreshOutcome, { ok: true }>['reason']): boolean {
  return reason === 'no-refresh-token' || reason === 'refresh-rejected'
}

/**
 * F4 hardening (adversarial review): this lane refreshes OUTSIDE
 * sessionExpiry's single-flight, so the shell watcher may have healed the
 * session while our attempt was failing. Before any clearSession()+redirect,
 * check whether a DIFFERENT live session now exists — different from the one
 * we started with AND from any token our own refresh minted (else the
 * refetch-rejected case would see its own fresh token and loop). If someone
 * else healed it, killing it would fight the self-heal lane.
 */
function healedElsewhere(started: Session, outcome: Extract<RefreshOutcome, { ok: false }>): boolean {
  const now = loadSession()
  return !!now && now.idToken !== started.idToken && now.idToken !== outcome.mintedIdToken
}

async function tryRefreshAndRefetch(session: Session, base: string): Promise<RefreshOutcome> {
  if (!session.refreshToken) return { ok: false, reason: 'no-refresh-token' }
  const username =
    (session.claims['cognito:username'] as string | undefined) ||
    session.claims.email ||
    session.claims.sub
  let fresh
  try {
    fresh = await refreshSession(username, session.refreshToken)
  } catch (err) {
    const transient = err instanceof AuthFlowError && err.code === 'Network'
    return { ok: false, reason: transient ? 'refresh-transient' : 'refresh-rejected' }
  }
  storeSession(fresh) // persist so the whole app uses the fresh tokens
  try {
    return { ok: true, data: await fetchConsumerOrders(fresh.idToken, base) }
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof ApiAuthError ? 'refetch-rejected' : 'refetch-failed',
      mintedIdToken: fresh.idToken,
    }
  }
}

/** Throttle for the focus/visibility background refetch: a wake within this
 *  window of the last fetch START is skipped (mirrors the 15s reauth-throttle
 *  idiom below — one knob, exported so tests pin both sides of it).
 *  BOARDROOM-2026-09-03-053306 follow-up: nothing refetched orders in a
 *  running SPA session (refresh() fires only after an assign submit), so a
 *  tab left open across a purchase showed the pre-purchase list until a full
 *  reload — Duncan's literal purchase-test symptom. */
export const FOCUS_REFETCH_MIN_INTERVAL_MS = 30_000

export function DataProvider({ children }: { children: ReactNode }) {
  const session = loadSession()
  const [liveState, setLiveState] = useState<LiveDataState>('unconfigured')
  const [live, setLive] = useState<LiveData>({ orders: [], tickets: [], events: [] })
  const [branding, setBranding] = useState<Record<string, OrgBrandingResolved | null>>({})
  const [tick, setTick] = useState(0)
  // Background-refetch bookkeeping (refs — never render-driving): when the
  // last fetch STARTED, and whether one is in flight (one at a time; a
  // foreground load counts, so a wake right after mount/refresh() is a no-op).
  const lastFetchAtRef = useRef(0)
  const inFlightRef = useRef(false)

  useEffect(() => {
    if (!session) return
    const base = ticketsApiBase()
    if (!base) {
      setLiveState('unconfigured')
      return
    }
    let cancelled = false
    setLiveState('loading')

    const load = async () => {
      lastFetchAtRef.current = Date.now()
      inFlightRef.current = true
      try {
        const data = await fetchConsumerOrders(session.idToken, base)
        if (cancelled) return
        setLive(data)
        setLiveState('ready')
      } catch (err) {
        if (cancelled) return
        // 401/403 = the id token was rejected (expired, or minted before a
        // server-side authorizer fix). Do NOT reload on the same stale token
        // (BOARDROOM-105741) — first REFRESH via the Cognito refresh token and
        // re-fetch with the fresh id token. A normal expiry self-heals silently.
        if (err instanceof ApiAuthError) {
          const outcome = await tryRefreshAndRefetch(session, base)
          if (cancelled) return
          if (outcome.ok) {
            setLive(outcome.data)
            setLiveState('ready')
            return
          }
          // TRANSIENT refresh failure, or the refresh HEALED the session and
          // only the re-fetch broke non-auth: the session is not (proven)
          // dead — honest unavailable, session kept, NO sign-out
          // (CMT-2026-09-03-026: redirect only when genuinely unrefreshable;
          // previously this lane cleared + reloaded even on a network blip).
          if (!isSessionEnd(outcome.reason) && outcome.reason !== 'refetch-rejected') {
            console.error('[DataProvider] orders re-auth could not complete (transient) — showing unavailable', {
              error: err, reason: outcome.reason,
            })
            setLiveState('unavailable')
            return
          }
          // F4 guard: the shell watcher may have healed the session while our
          // refresh was failing — never kill a session another lane just
          // minted; re-run the load on it instead.
          if (healedElsewhere(session, outcome)) {
            setTick((t) => t + 1)
            return
          }
          // SESSION END (no/rejected refresh token) or the FRESH token is also
          // rejected → VISIBLE redirect to sign-in with the return path
          // preserved (CMT-2026-09-03-026 — never an open-but-detached rest
          // state, never a bare reload that loses the path). Throttled so a
          // persistently-rejecting token can't loop navigation; inside the
          // window we show the honest holding state instead of a redirect storm.
          const KEY = 'mytrumin.reauth.at'
          const last = Number(sessionStorage.getItem(KEY) || 0)
          if (Date.now() - last > 15000) {
            sessionStorage.setItem(KEY, String(Date.now()))
            clearSession() // SESSION record only — drafts/extras/child records untouched
            redirectToSignIn()
            return
          }
          console.error('[DataProvider] re-auth exhausted — orders unavailable', { error: err })
          setLiveState('unavailable')
          return
        }
        // HONEST DEGRADE (P0 BOARDROOM-2026-09-03-053306): a failed refresh is
        // never silent. The screens drop to the 'unavailable' notice (the
        // stale `live` snapshot is never presented as fresh — every consumer
        // gates on liveState), and the CAUSE lands structured on the console
        // so a field report carries the reason, not just the symptom.
        console.error('[DataProvider] consumer orders refresh failed — showing unavailable, not stale data', {
          error: err,
        })
        setLiveState('unavailable')
      } finally {
        inFlightRef.current = false
      }
    }
    void load()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])

  // RETURN-TO-TAB BACKGROUND REFETCH (BOARDROOM-2026-09-03-053306 follow-up):
  // when the tab becomes visible / the window regains focus, quietly re-pull
  // the orders wire so a purchase made in another tab appears without a
  // reload. Strictly non-degrading:
  //  - SILENT — liveState never flips to 'loading'; on success the data swaps
  //    in place exactly like refresh() ('ready' + fresh snapshot).
  //  - THROTTLED — skipped inside FOCUS_REFETCH_MIN_INTERVAL_MS of the last
  //    fetch start (foreground loads count), one in flight at a time.
  //  - FAILURE keeps the current view untouched: structured console.error
  //    only — a background poke must never blank a working screen. An auth
  //    reject self-heals via the refresh token (BOARDROOM-105741 idiom).
  //    ONE amendment (Duncan rule CMT-2026-09-03-026): when the refresh
  //    itself is REFUSED (session genuinely ended) the wake redirects
  //    VISIBLY to sign-in with the return path preserved — never an
  //    open-but-detached tab. Every ambiguous failure still keeps the view.
  useEffect(() => {
    let disposed = false
    const wake = () => {
      if (document.visibilityState !== 'visible') return
      const s = loadSession() // fresh read — picks up tokens persisted since mount
      const base = ticketsApiBase()
      if (!s || !base) return
      if (inFlightRef.current) return
      if (Date.now() - lastFetchAtRef.current < FOCUS_REFETCH_MIN_INTERVAL_MS) return
      lastFetchAtRef.current = Date.now()
      inFlightRef.current = true
      void (async () => {
        try {
          const data = await fetchConsumerOrders(s.idToken, base)
          if (disposed) return
          setLive(data)
          setLiveState('ready')
        } catch (err) {
          if (err instanceof ApiAuthError) {
            const outcome = await tryRefreshAndRefetch(s, base)
            if (disposed) return
            if (outcome.ok) {
              setLive(outcome.data)
              setLiveState('ready')
              return
            }
            // SESSION END on a wake (laptop slept past the refresh token, or
            // it was revoked): the tab must not sit open-but-detached — the
            // Duncan rule (CMT-2026-09-03-026) makes this the ONE background
            // case that navigates, and only AFTER the refresh itself was
            // refused. Ambiguous failures (network, authorizer-side rejects)
            // still never blank or leave a working screen.
            if (isSessionEnd(outcome.reason) && !healedElsewhere(s, outcome)) {
              clearSession() // session record only — device-local data untouched
              redirectToSignIn()
              return
            }
          }
          console.error('[DataProvider] background orders refetch failed — keeping the current view', {
            error: err,
          })
        } finally {
          inFlightRef.current = false
        }
      })()
    }
    window.addEventListener('focus', wake)
    document.addEventListener('visibilitychange', wake)
    return () => {
      disposed = true
      window.removeEventListener('focus', wake)
      document.removeEventListener('visibilitychange', wake)
    }
  }, [])

  // Best-effort org-unit branding (BOARDROOM-2026-08-28-110128): for each
  // distinct REAL org-unit id on the loaded orders, read settings' resolved
  // branding once. Synthesised `org-{name}` fallback ids are never sent (the
  // endpoint 400s on non-UUIDs), fetch failures leave the neutral fallback in
  // place, and a 404 is cached as null so it is not re-fetched every render.
  useEffect(() => {
    if (!session) return
    const base = orgBrandingApiBase()
    if (!base) return
    const wanted = [...new Set(live.events.map((e) => e.organiser.id))].filter(
      (id) => isOrgUnitUuid(id) && !(id in branding),
    )
    if (wanted.length === 0) return
    let cancelled = false
    void Promise.all(
      wanted.map(async (id) => {
        try {
          return [id, await fetchOrgBranding(id, base, session.idToken)] as const
        } catch {
          return null // transient failure — leave unfetched so a later load retries
        }
      }),
    ).then((pairs) => {
      if (cancelled) return
      const landed = pairs.filter((p): p is readonly [string, OrgBrandingResolved | null] => p !== null)
      if (landed.length) setBranding((prev) => ({ ...prev, ...Object.fromEntries(landed) }))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.events])

  // Apply the resolved accent as the organiser identity colour. Events without
  // a resolved accent keep accentFg '' — consumers render the neutral fallback
  // (never a status hue, README §Status colours).
  const events = useMemo<TrEvent[]>(
    () =>
      live.events.map((e) => {
        const accent = branding[e.organiser.id]?.organiserAccent
        return accent ? { ...e, organiser: { ...e.organiser, accentFg: accent } } : e
      }),
    [live.events, branding],
  )

  const value = useMemo<DataContextValue>(() => ({
    liveState,
    orders: live.orders,
    tickets: live.tickets,
    events,
    eventById: (id) => events.find((e) => e.id === id),
    ticketsForOrder: (orderId) => live.tickets.filter((t) => t.orderId === orderId),
    orgBranding: (orgUnitId) => branding[orgUnitId],
    sessionEmail: session?.claims.email,
    refresh: () => setTick((t) => t + 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [liveState, live, events, branding])

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export function useData(): DataContextValue {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useData outside DataProvider')
  return ctx
}

/** Optional reader — null outside a provider (component tests / isolated flow
 *  mounts), mirroring useGetReadyLiveOptional. Callers degrade to their
 *  neutral fallback instead of crashing (V1.9.1 P4: the waiver flow's
 *  organiser-accent read). */
export function useDataOptional(): DataContextValue | null {
  return useContext(DataContext)
}

/** Honest status panel while the backing service is loading or absent. */
export function LiveDataNotice({ liveState }: { liveState: LiveDataState }) {
  // BOARDROOM-2026-08-15-090842: env-AGNOSTIC copy — these strings render on PROD,
  // so they must never say "stage" (that read wrong + unprofessional to Duncan's
  // prod test). No environment word at all; the state is what matters.
  const message =
    liveState === 'loading'
      ? 'Loading your orders…'
      : liveState === 'unavailable'
        ? 'Your orders are temporarily unavailable — they will appear the moment the service is back.'
        : 'The orders service is not connected in this build — your real orders appear the moment it is.'
  return (
    <div role="status" data-wired="false" style={{ border: '1px dashed var(--text-faint)', borderRadius: 'var(--r-card)', padding: 24, color: 'var(--text-secondary)', fontSize: 13, marginTop: 12 }}>
      {message}
    </div>
  )
}
